import { BaseModel } from "./BaseModel";
import { ColumnHeaders, ReportNames, TabNames } from '../modules/Constants';

export class BOMModel extends BaseModel {
    SaleablePart = undefined;

    supplementalFormatting(formatting) {
        formatting.columns[9].width = 200;
        formatting.columns[18].width = 300;
        formatting.columns[31].width = 300;
        formatting.columns[37].width = 300;

        return formatting;
    }

    constructor() {
        super();
        this.TabName = TabNames.BOM_COMPOSITE;
        this.TabIndex = 1;
        this.DataProcessor = new BOMProcessor();
    }

    static PartSpecificBOM(saleablePart, data, index) {
        let result = new BOMModel();
        result.SaleablePart = saleablePart
        result.TabIndex = index;
        result.TabName = `${saleablePart?.Part?.Number?.Fields[0] ?? ""}-${saleablePart?.Part?.Suffix?.Fields[0]?.Fields[0] ?? ""},${saleablePart?.Part?.DrawingRevision?.Fields[0]?.Fields[0] ?? ""} ${ReportNames.BOM}`


        if (result.TabName.length > 31) {
            result.TabName = "Tab" + result.TabIndex;
        }


        //Filter downm Rows to only be the rows that are for this Part.
        let filteredData = data.display.filter(row => {
            return row[1] === saleablePart?.Part?.Number?.Fields[0] &&
                row[2] === saleablePart?.Part?.DrawingRevision?.Fields[0]?.Fields[0] &&
                row[3] === saleablePart?.Part?.Suffix?.Fields[0]?.Fields[0]
        });

        let display = [];
        display.push(data.headers);
        filteredData.forEach(row => {
            display.push(row);
        })

        result.Headers = data.headers;
        result.Display = display;
        result.Numerics = data.numerics;

        return result;
    }
}

export class BOMProcessor {
    process(data) {
        // -- Define Variables
        let exchangeRates = [];
        let records = [];
        let headers = [];
        let currency = data.currency;
        let logisticsTotalUsageVolumeYears = [];
        let returnableDunnageInvestmentYears = [];

        let snapshots = data.snapshots;
        let version = data.version;
        let study = data.study;

        // -- Get Exchange Rates
        study.exchangeRates.forEach(rate => {
            rate.Item2.Values.forEach(item => {
                exchangeRates.push({
                    currency: rate.Item1,
                    year: new Date(item.Date).getUTCFullYear(),
                    value: item.Value
                });
            });
        });


        // -- Set Years
        let firstSOPYear = Math.min.apply(Math, snapshots.map(snapshot => snapshot.SaleableParts?.map(part => new Date(part?.CustomerSOP?.Value.Fields[0]).getUTCFullYear())).reduce(function (a, b) { return a.concat(b) }, []));
        let firstYearSpend = firstSOPYear - (version?.SpendYearsPriorToSOP?.Value?.Fields[0] ?? 2);
        let lastYearSpend = firstSOPYear + (version?.SpendYearsAfterSOP?.Value?.Fields[0] ?? 0);
        snapshots.forEach(snapshot => {
            snapshot.SaleableParts.forEach(part => {
                let endDate = part.CustomerVolume?.Value?.Fields[0]?.EndDate;
                if (endDate) {
                    let year = new Date(endDate).getUTCFullYear();
                    if (year > lastYearSpend) {
                        lastYearSpend = year;
                    }
                }
            });
        })

        let yearsSpending = Array.from(Array((lastYearSpend - firstYearSpend) + 1), (_, x) => x + firstYearSpend);
        logisticsTotalUsageVolumeYears = yearsSpending.map(x => x);
        returnableDunnageInvestmentYears = yearsSpending.map(x => x);

        // -- Set Additional logisticsTotalUsageVolumeYears & returnableDunnageInvestmentYears
        snapshots.forEach(snapshot => {
            let logistics = snapshot.Logistics

            logistics.forEach(logistic => {
                let volumes = logistic?.TotalUsageVolume?.Fields[0]?.Values

                volumes?.forEach(volume => {
                    let year = new Date(volume.Date).getUTCFullYear();

                    if (logisticsTotalUsageVolumeYears.findIndex(x => x === year) <= -1) {
                        logisticsTotalUsageVolumeYears.push(year);
                    }
                })

                let returnableDunnage = logistic?.ReturnableDunnageInvestment?.Value?.Fields[0]?.Values;
                returnableDunnage?.forEach(volume => {
                    let year = new Date(volume.Date).getUTCFullYear();

                    if (returnableDunnageInvestmentYears.findIndex(x => x === year) <= -1) {
                        returnableDunnageInvestmentYears.push(year);
                    }
                })
            })
        })
        logisticsTotalUsageVolumeYears.sort();
        returnableDunnageInvestmentYears.sort();

        // -- Set Records
        records = this.setRecords(exchangeRates, currency, snapshots, logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears);

        // -- Set Headers
        headers = this.configureHeader(logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears);

        // -- Get Rows
        let result = {
            headers: headers,
            display: this.getDisplay(headers, records, currency, logisticsTotalUsageVolumeYears),
            numerics: this.getNumerics(logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears)
        };
        return result;
    }

    setRecords(exchangeRates, currency, snapshots, logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears) {
        let records = [];
        snapshots.forEach(snapshot => {
            //-- Get the Saleable Parts
            let parts = snapshot.SaleableParts;

            parts.forEach(saleablePart => {
                //-- Get the Component Parts
                let bom = saleablePart.Bom.value.parts.concat(...saleablePart.Bom.value.partGroups)

                bom.forEach((part, index) => {
                    //Get the Purchase for the Comp. Part.
                    let purchase = snapshot.Purchases?.find(purchase => 
                        // purchase.Name.Value.Fields[0] === part.name.value &&
                        purchase.Part.Number.Fields[0] === part.partId.partNumber &&
                        (purchase.Part.DrawingRevision === null || purchase.Part.DrawingRevision.Fields[0]?.Fields[0] === part.partId.drawingRevision) &&
                        (purchase.Part.Suffix === null || purchase.Part.Suffix.Fields[0]?.Fields[0] === part.partId.suffix)
                    );

                    //Get the Logistics for the Comp. Part.
                    let logistics = snapshot.Logistics?.find(logistic => 
                        // logistic.Name.Value.Fields[0] === part.name.value && 
                        logistic.Part.Number.Fields[0] === part.partId.partNumber &&
                        (logistic.Part.DrawingRevision === null || logistic.Part.DrawingRevision.Fields[0]?.Fields[0] === part.partId.drawingRevision) &&
                        (logistic.Part.Suffix === null || logistic.Part.Suffix.Fields[0]?.Fields[0] === part.partId.suffix)
                    );

                    //Get Row Models for each Comp. Part
                    if (part.manufacturingVolumeAllocationShares.length > 0) {

                        //Determine a unique list of Mfg. Volume Allocation Shares for each Comp. Part.
                        let uniqueMfgAllocationShares = [];
                        part.manufacturingVolumeAllocationShares.forEach(item => {
                            var i = uniqueMfgAllocationShares.findIndex(x => x.name === item.name && x.allocationShare === item.allocationShare);
                            if (i <= -1) {
                                uniqueMfgAllocationShares.push({ name: item.name, allocationShare: item.allocationShare });
                            }
                        })
                        uniqueMfgAllocationShares.forEach(allocation => {
                            records.push(new BOMRowModel(exchangeRates, currency, logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears, part, saleablePart, index, purchase, logistics, allocation));
                        })
                    } else {
                        records.push(new BOMRowModel(exchangeRates, currency, logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears, part, saleablePart, index, purchase, logistics, null));
                    }
                });
            })
        });

        return records;
    }

    configureHeader(logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears) {
        let header = [
            ColumnHeaders.OPTION_NAME,
            ColumnHeaders.SALEABLE_PART_NUMBER,
            ColumnHeaders.SALEABLE_PART_REV,
            ColumnHeaders.SALEABLE_PART_SUFFIX,
            ColumnHeaders.SORT_ORDER,
            ColumnHeaders.BOM_LEVEL,
            ColumnHeaders.COMPONENT_PART_NUMBER,
            ColumnHeaders.COMPONENT_PART_REV,
            ColumnHeaders.COMPONENT_PART_SUFFIX,
            ColumnHeaders.COMPONENT_NAME,
            ColumnHeaders.QTY_PER_PARENT,
            ColumnHeaders.QTY_PER_SALEABLE,
            ColumnHeaders.UOM,
            ColumnHeaders.PRODUCTION_METHOD,
            ColumnHeaders.MFG_PLANT,
            ColumnHeaders.RECV_PLANT,
            ColumnHeaders.DSS_INCLUDED,
            ColumnHeaders.MAINTAIN_FOR_SERVICE,
            ColumnHeaders.PRODCUT_ENGINEER_COMMENTS,
            ColumnHeaders.MATERIAL_TYPE,
            ColumnHeaders.MATERIAL_MASS,
            ColumnHeaders.MATERIAL_MASS_UOM,
            ColumnHeaders.INCOTERMS,
            ColumnHeaders.TITLE_TRANSFER_TERMS,
            ColumnHeaders.LEAD_TIME_WEEKS,
            ColumnHeaders.SUPPLIER_NAME,
            ColumnHeaders.SUPPLIER_LOCATION,
            ColumnHeaders.LOGISTICS_LOCATION,
            ColumnHeaders.PURCHASE_DATA_TYPE,
            ColumnHeaders.SOURCING_RISK,
            ColumnHeaders.REACH_ASSUMPTIONS,
            ColumnHeaders.PURCHASING_COMMENTS,
            ColumnHeaders.PACKAGING_TYPE,
            ColumnHeaders.NESTED,
            ColumnHeaders.EXPORT_READY,
            ColumnHeaders.INVENTORY_DAYS,
            ColumnHeaders.WORKING_DAYS_PER_YEAR,
            ColumnHeaders.LOGISTICS_COMMENTS,
            `${ColumnHeaders.LOGISTICS_TOTAL_USAGE_VOLUME_PREFIX} ${ColumnHeaders.START_YEAR}`
        ]

        logisticsTotalUsageVolumeYears.forEach(year => {
            header.push(`${year} ${ColumnHeaders.LOGISTICS_TOTAL_USAGE_VOLUME_PREFIX}`);
        });

        header.push(
            ColumnHeaders.MFG_VOLUME_ALLOCATION_SHARE,
            ColumnHeaders.MFG_VOLUME_ALLOCATION_SHARE_PROCESS_NAME,
            ColumnHeaders.MATERIAL_COST,
            ColumnHeaders.MATERIAL_COST_PER_ASM,
            `${ColumnHeaders.MATERIAL_COST} ${ColumnHeaders.CURRENCY_PREFIX}`,
            `${ColumnHeaders.MATERIAL_COST} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.MATERIAL_COST_PER_ASM} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.MATERIAL_COST} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
            ColumnHeaders.FREIGHT_COST,
            ColumnHeaders.FREIGHT_COST_PER_ASM,
            `${ColumnHeaders.FREIGHT_COST} ${ColumnHeaders.CURRENCY_PREFIX}`,
            `${ColumnHeaders.FREIGHT_COST} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.FREIGHT_COST_PER_ASM} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.FREIGHT_COST} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
            ColumnHeaders.DUTY_PERCENT,
            ColumnHeaders.DUTY_DRAWBACK_PERCENT,
            `${ColumnHeaders.DUTY_COST_PER_ASM} ${ColumnHeaders.CONVERTED_PREFIX}`,
            ColumnHeaders.EXPENDABLE_DUNNAGE,
            ColumnHeaders.PACKAGING_COST,
            ColumnHeaders.WAREHOUSE_COST,
            ColumnHeaders.WAREHOUSE_COST_PER_ASM,
            `${ColumnHeaders.WAREHOUSE_COST} ${ColumnHeaders.CURRENCY_PREFIX}`,
            `${ColumnHeaders.WAREHOUSE_COST} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.WAREHOUSE_COST_PER_ASM} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.WAREHOUSE_COST} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
            ColumnHeaders.PACKAGING_COST_PER_ASM,
            `${ColumnHeaders.PACKAGING_COST} ${ColumnHeaders.CURRENCY_PREFIX}`,
            `${ColumnHeaders.PACKAGING_COST} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.PACKAGING_COST_PER_ASM} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.PACKAGING_COST} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
            ColumnHeaders.EXPENDABLE_DUNNAGE_PER_ASM,
            `${ColumnHeaders.EXPENDABLE_DUNNAGE} ${ColumnHeaders.CURRENCY_PREFIX}`,
            `${ColumnHeaders.EXPENDABLE_DUNNAGE} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.EXPENDABLE_DUNNAGE_PER_ASM} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.EXPENDABLE_DUNNAGE} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
        );


        returnableDunnageInvestmentYears.forEach(year => {
            header.push(`${year} ${ColumnHeaders.RETURNABLE_DUNNAGE_INVESTMENT_PREFIX}`);
        });

        header.push(
            `${ColumnHeaders.RETURNABLE_DUNNAGE_INVESTMENT_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
        );

        returnableDunnageInvestmentYears.forEach(year => {
            header.push(`${year} ${ColumnHeaders.RETURNABLE_DUNNAGE_INVESTMENT_PREFIX} ${ColumnHeaders.CONVERTED_PREFIX}`);
        });

        header.push(
            `${ColumnHeaders.TOTAL_RETURNABLE_DUNNAGE_INVESTMENT} ${ColumnHeaders.CONVERTED_PREFIX}`,
            `${ColumnHeaders.RETURNABLE_DUNNAGE_INVESTMENT_PREFIX} ${ColumnHeaders.CONVERTED_PREFIX} ${ColumnHeaders.CURRENCY_PREFIX}`,
            ColumnHeaders.LOGISTICS_VOLUME_ALLOCTION_SHARE,
            `${ColumnHeaders.VOLUME_ALLOCATED_TOTAL_RETURNABLE_DUNNAGE_INVESTMENT} ${ColumnHeaders.CONVERTED_PREFIX}`
        );

        return header;
    }

    getDisplay(headers, records, currency, logisticsTotalUsageVolumeYears) {
        let display = [];

        display.push(headers);

        records.forEach(record => {
            let row = [
                record.OptionName,
                record.SaleablePartNumber,
                record.SaleablePartRev,
                record.SaleablePartSuffix,
                record.SortOrder,
                record.BOMLev,
                record.ComponentPartNumber,
                record.ComponentPartRevision,
                record.ComponentPartSuffix,
                record.ComponentName,
                record.QuantityPerParent,
                record.QuantityPerSaleable,
                record.UoM,
                record.ProdMethod,
                record.MfgPlant,
                record.RecvPlant,
                record.DSSIncluded,
                record.MaintainForService,
                record.ProductEngineerComments,
                record.MaterialType,
                record.MaterialMass,
                record.MaterialMassUoM,
                record.Incoterms,
                record.TitleTransferTerms,
                record.LeadTimeWeeks,
                record.SupplierName,
                record.SupplierLocation,
                record.LogisticsLocation,
                record.PurchaseDataType,
                record.SourcingRisk,
                record.ReachAssumptions,
                record.PurchasingComments,
                record.PackagingType,
                record.Nested,
                record.ExportReady,
                record.InventoryDays,
                record.WorkingDaysPerYear,
                record.LogisticsComments,
                logisticsTotalUsageVolumeYears[0]
            ]

            record.LogisticsTotalUsageVolumeValues.forEach(val => {
                row.push(val);
            });

            row.push(
                record.MfgVolumeAllocationShare,
                record.MfgVolumeAllocationShareProcessName,
                record.MaterialCost,
                record.MaterialCostPerAsm,
                record.MaterialCostCurrency,
                record.MaterialCostConvCurr,
                record.MaterialCostPerAsmConvCurr,
                currency,
                record.FreightCost,
                record.FreightCostPerAsm,
                record.FreightCostCurrency,
                record.FreightCostConvCurr,
                record.FreightCostPerAsmConvCurr,
                currency,
                record.DutyPercent,
                record.DutyDrawbackPercent,
                record.DutyCostPerAsmConvCurr,
                record.ExpendableDunnage,
                record.PackagingCost,
                record.WarehouseCost,
                record.WarehouseCostPerAsm,
                record.WarehouseCostCurrency,
                record.WarehouseCostConvCurr,
                record.WarehouseCostPerAsmConvCurr,
                currency,
                record.PackagingCostPerAsm,
                record.PackagingCostCurrency,
                record.PackagingCostConvCurr,
                record.PackagingCostPerAsmConvCurr,
                currency,
                record.ExpendableDunnagePerAsm,
                record.ExpendableDunnageCurrency,
                record.ExpendableDunnageConvCurr,
                record.ExpendableDunnagePerAsmConvCurr,
                currency,
            )

            record.ReturnableDunnageInvestmentValues.forEach(val => {
                row.push(val);
            });

            row.push(
                record.ReturnableDunnageInvestmentCurrency,
            )

            record.ReturnableDunnageInvestmentValuesConvCurr.forEach(val => {
                row.push(val);
            });

            row.push(
                record.ReturnableDunnageInvestmentCurrencyConvCurrTotal,
                currency,
                record.LogisticsVolumeAllocationShare,
                record.VolumeAllocatedTotalReturnableDunnageInvestmentConvCurr
            )

            display.push(row);
        });

        return display;
    }

    getNumerics(logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears) {
        let numeric = [];

        //set number formatting
        let logisticsVolumeStart = 39;
        let logisticsVolumeEnd = logisticsTotalUsageVolumeYears.length + logisticsVolumeStart;
        let materialCostStart = logisticsVolumeEnd + 2
        let materialCostEnd = materialCostStart + 1;
        let materialCostConvStart = materialCostEnd + 2
        let materialCostConvEnd = materialCostConvStart + 1;
        let freightCostStart = materialCostConvEnd + 2;
        let freightCostEnd = freightCostStart + 1;
        let freightCostConvStart = freightCostEnd + 2
        let freightCostConvEnd = freightCostConvStart + 1;
        let dutyStart = freightCostConvEnd + 2;
        let warehouseCostEnd = dutyStart + 6;
        let warehouseCostConvStart = warehouseCostEnd + 2;
        let warehouseCostConvEnd = warehouseCostConvStart + 1;
        let packagingCostStart = warehouseCostConvEnd + 1;
        let packagingCostEnd = packagingCostStart + 1;
        let packagingCostConvStart = packagingCostEnd + 2;
        let packagingCostConvEnd = packagingCostConvStart + 1;
        let expendableDunnageStart = packagingCostConvEnd + 1;
        let expendableDunnageEnd = expendableDunnageStart + 1;
        let expendableDunnageConvStart = expendableDunnageEnd + 2;
        let expendableDunnageConvEnd = expendableDunnageConvStart + 1;
        let returnableDunnageInvestmentStart = expendableDunnageConvEnd + 2;
        let returnableDunnageInvestmentEnd = returnableDunnageInvestmentStart + returnableDunnageInvestmentYears.length - 1;
        let returnableDunnageInvestmentConvStart = returnableDunnageInvestmentEnd + 2
        let returnableDunnageInvestmentConvEnd = returnableDunnageInvestmentConvStart + returnableDunnageInvestmentYears.length;
        let logisticsVolumeShareStart = returnableDunnageInvestmentConvEnd + 2;
        let logisticsVolumeShareEnd = logisticsVolumeShareStart + 1


        numeric.push({ start: logisticsVolumeStart, end: logisticsVolumeEnd });
        numeric.push({ start: materialCostStart, end: materialCostEnd });
        numeric.push({ start: materialCostConvStart, end: materialCostConvEnd });
        numeric.push({ start: freightCostStart, end: freightCostEnd });
        numeric.push({ start: freightCostConvStart, end: freightCostConvEnd });
        numeric.push({ start: dutyStart, end: warehouseCostEnd });
        numeric.push({ start: warehouseCostConvStart, end: warehouseCostConvEnd });
        numeric.push({ start: packagingCostStart, end: packagingCostEnd });
        numeric.push({ start: packagingCostConvStart, end: packagingCostConvEnd });
        numeric.push({ start: expendableDunnageStart, end: expendableDunnageEnd });
        numeric.push({ start: expendableDunnageConvStart, end: expendableDunnageConvEnd });
        numeric.push({ start: returnableDunnageInvestmentStart, end: returnableDunnageInvestmentEnd });
        numeric.push({ start: returnableDunnageInvestmentConvStart, end: returnableDunnageInvestmentConvEnd });
        numeric.push({ start: logisticsVolumeShareStart, end: logisticsVolumeShareEnd });

        return numeric;
    }
}
export class BOMRowModel {

    //-- Define Properties
    OptionName = "";
    SaleablePartNumber = "";
    SaleablePartRev = "";
    SaleablePartSuffix = "";
    SortOrder = "";
    BOMLev = "";
    ComponentPartNumber = "";
    ComponentPartRevision = "";
    ComponentPartSuffix = "";
    ComponentName = "";
    QuantityPerParent = "";
    QuantityPerSaleable = "";
    UoM = "";
    ProdMethod = "";
    MfgPlant = "";
    RecvPlant = "";
    DSSIncluded = "";
    MaintainForService = "";
    ProductEngineerComments = "";
    MaterialType = "";
    MaterialMass = "";
    MaterialMassUoM = "";
    Incoterms = "";
    TitleTransferTerms = "";
    LeadTimeWeeks = "";
    SupplierName = "";
    SupplierLocation = "";
    LogisticsLocation = "";
    PurchaseDataType = "";
    SourcingRisk = "";
    ReachAssumptions = "";
    PurchasingComments = "";
    PackagingType = "";
    Nested = "";
    ExportReady = "";
    InventoryDays = "";
    WorkingDaysPerYear = "";
    LogisticsComments = "";
    LogisticsTotalUsageVolumeValues = [];
    MfgVolumeAllocationShare = "";
    MfgVolumeAllocationShareProcessName = "";
    MaterialCost = "";
    MaterialCostPerAsm = "";
    MaterialCostCurrency = "";
    MaterialCostConvCurr = "";
    MaterialCostPerAsmConvCurr = "";
    MaterialCostDisplayCurrency = "";
    FreightCost = "";
    FreightCostPerAsm = "";
    FreightCostCurrency = "";
    FreightCostConvCurr = "";
    FreightCostPerAsmConvCurr = "";
    FreightCostDisplayCurrency = "";
    DutyPercent = "";
    DutyDrawbackPercent = "";
    DutyCostPerAsmConvCurr = "";
    ExpendableDunnage = "";
    PackagingCost = "";
    WarehouseCost = "";
    WarehouseCostPerAsm = "";
    WarehouseCostCurrency = "";
    WarehouseCostConvCurr = "";
    WarehouseCostPerAsmConvCurr = "";
    WarehouseCostDisplayCurrency = "";
    PackagingCostPerAsm = "";
    PackagingCostCurrency = "";
    PackagingCostConvCurr = "";
    PackagingCostPerAsmConvCurr = "";
    PackagingCostDisplayCurrency = "";
    ExpendableDunnagePerAsm = "";
    ExpendableDunnageCurrency = "";
    ExpendableDunnageConvCurr = "";
    ExpendableDunnagePerAsmConvCurr = "";
    ExpendableDunnageDisplayCurrency = "";
    ReturnableDunnageInvestmentValues = [];
    ReturnableDunnageInvestmentValuesConvCurr = [];
    ReturnableDunnageInvestmentCurrency = "";
    ReturnableDunnageInvestmentCurrencyConvCurrTotal = 0;
    LogisticsVolumeAllocationShare = "";
    VolumeAllocatedTotalReturnableDunnageInvestmentConvCurr = 0;

    constructor(exchangeRates, exchangeCurrency, logisticsTotalUsageVolumeYears, returnableDunnageInvestmentYears, part, saleablePart, index, purchase, logistics, allocation) {
        //Set Properties
        this.OptionName = part?.groupName;
        this.SaleablePartNumber = saleablePart?.Part?.Number?.Fields[0];
        this.SaleablePartRev = saleablePart?.Part?.DrawingRevision?.Fields[0]?.Fields[0];
        this.SaleablePartSuffix = saleablePart?.Part?.Suffix?.Fields[0]?.Fields[0];
        this.SortOrder = index;
        this.BOMLev = part?.bomLevel?.value;
        this.ComponentPartNumber = part?.partId?.partNumber;
        this.ComponentPartRevision = part?.partId?.drawingRevision;
        this.ComponentPartSuffix = part?.partId?.suffix;
        this.ComponentName = part?.name?.value;
        this.QuantityPerParent = part?.quantityPerParent?.value;
        this.QuantityPerSaleable = part?.quantityPerSaleable;
        this.UoM = part?.unitOfMeasure?.value;
        this.ProdMethod = part?.productionMethod?.value?.method;
        this.MfgPlant = part?.productionMethod?.value?.manufacturingPlant?.value?.plantNumber;
        this.RecvPlant = part?.productionMethod?.value?.receivingPlant?.value?.plantNumber;
        this.DSSIncluded = part?.dssIncluded?.value;
        this.MaintainForService = part?.maintainForService?.value;
        this.ProductEngineerComments = part?.peComments?.value;
        this.MaterialType = part?.materialType?.value;
        this.MaterialMass = Number.isNaN(Number(part?.materialMass?.value)) ? "" : part?.materialMass?.value;
        this.MaterialMassUoM = part?.materialMassUOM?.value;
        this.Incoterms = purchase?.Incoterms?.Value?.Fields[0]?.Case;
        this.TitleTransferTerms = purchase?.TitleTransferTerms?.Value?.Fields[0]?.Case;
        this.LeadTimeWeeks = purchase?.LeadTime?.Value?.Fields[0];
        this.SupplierName = purchase?.SupplierName?.Value?.Fields[0];
        this.SupplierLocation = purchase?.SupplierLocation?.Value?.Fields[0];
        this.LogisticsLocation = purchase?.LogisticsLocation?.Value?.Fields[0]?.Fields[0];
        this.PurchaseDataType = purchase?.PurchaseDataType?.Value?.Fields[0]?.Case;
        this.SourcingRisk = purchase?.SourcingRisk?.Value?.Fields[0];
        this.ReachAssumptions = purchase?.ReachAssumptions?.Value?.Fields[0];
        this.PurchasingComments = purchase?.PurchasingComments?.Value?.Fields[0];
        this.PackagingType = logistics?.PackagingType?.Value?.Fields[0].Case;
        this.Nested = logistics?.Nested?.Value?.Fields[0];
        this.ExportReady = logistics?.ExportReady?.Value?.Fields[0];
        this.InventoryDays = logistics?.InventoryDays?.Value?.Fields[0];
        this.WorkingDaysPerYear = logistics?.WorkingDaysPerYear?.Value?.Fields[0];
        this.LogisticsComments = logistics?.LogisticsComments?.Value?.Fields[0];
        this.LogisticsTotalUsageVolumeValues = [];
        this.DutyPercent = logistics?.DutyPercent?.Value?.Fields[0]?.Values[0]?.Value;
        this.DutyDrawbackPercent = logistics?.DutyDrawbackPercent?.Value?.Fields[0]?.Values[0]?.Value;
        this.ReturnableDunnageInvestmentValues = [];
        this.LogisticsVolumeAllocationShare = Number.isNaN(Number(part?.logisticsVolumeAllocationShare)) ? "" : part?.logisticsVolumeAllocationShare;


        if (allocation) {
            this.MfgVolumeAllocationShare = Number.isNaN(allocation.allocationShare) ? "" : allocation.allocationShare
            this.MfgVolumeAllocationShareProcessName = allocation.name
        }

        logisticsTotalUsageVolumeYears.forEach(year => {
            let value = undefined

            if (logistics) {
                value = logistics.TotalUsageVolume.Fields[0].Values.find(item => new Date(item.Date).getUTCFullYear() === year)?.Value ?? 0;
            }

            this.LogisticsTotalUsageVolumeValues.push(value);
        })


        let materialCost = purchase?.MaterialCost?.Value?.Fields[0];
        let materialCost_calcs = this.exchangeItem(materialCost, this.QuantityPerSaleable, exchangeRates, exchangeCurrency);
        this.MaterialCost = materialCost_calcs.Total;
        this.MaterialCostConvCurr = materialCost_calcs.ConvTotal;
        this.MaterialCostPerAsm = materialCost_calcs.PerAsm;
        this.MaterialCostPerAsmConvCurr = materialCost_calcs.PerAsmConv;
        this.MaterialCostCurrency = materialCost?.Tag;

        let freightCost = logistics?.FreightCost?.Value?.Fields[0];
        let freightCost_calcs = this.exchangeItem(freightCost, this.QuantityPerSaleable, exchangeRates, exchangeCurrency);
        this.FreightCost = freightCost_calcs.Total;
        this.FreightCostConvCurr = freightCost_calcs.ConvTotal;
        this.FreightCostPerAsm = freightCost_calcs.PerAsm;
        this.FreightCostPerAsmConvCurr = freightCost_calcs.PerAsmConv;
        this.FreightCostCurrency = freightCost?.Tag;

        let expendableDunnage = logistics?.ExpendableDunnage?.Value?.Fields[0];
        let expendableDunnage_calcs = this.exchangeItem(expendableDunnage, this.QuantityPerSaleable, exchangeRates, exchangeCurrency);
        this.ExpendableDunnage = expendableDunnage_calcs.Total;
        this.ExpendableDunnageConvCurr = expendableDunnage_calcs.ConvTotal;
        this.ExpendableDunnagePerAsm = expendableDunnage_calcs.PerAsm;
        this.ExpendableDunnagePerAsmConvCurr = expendableDunnage_calcs.PerAsmConv;
        this.ExpendableDunnageCurrency = expendableDunnage?.Tag;

        let packagingCost = logistics?.PackagingCost?.Value?.Fields[0];
        let packagingCost_calcs = this.exchangeItem(packagingCost, this.QuantityPerSaleable, exchangeRates, exchangeCurrency);
        this.PackagingCost = packagingCost_calcs.Total;
        this.PackagingCostConvCurr = packagingCost_calcs.ConvTotal;
        this.PackagingCostPerAsm = packagingCost_calcs.PerAsm;
        this.PackagingCostPerAsmConvCurr = packagingCost_calcs.PerAsmConv;
        this.PackagingCostCurrency = packagingCost?.Tag;

        let warehouseCost = logistics?.WarehouseCost?.Value?.Fields[0];
        let warehouseCost_calcs = this.exchangeItem(warehouseCost, this.QuantityPerSaleable, exchangeRates, exchangeCurrency);
        this.WarehouseCost = warehouseCost_calcs.Total;
        this.WarehouseCostConvCurr = warehouseCost_calcs.ConvTotal;
        this.WarehouseCostPerAsm = warehouseCost_calcs.PerAsm;
        this.WarehouseCostPerAsmConvCurr = warehouseCost_calcs.PerAsmConv;
        this.WarehouseCostCurrency = warehouseCost?.Tag;

        returnableDunnageInvestmentYears.forEach(year => {
            let cost = undefined;
            let convCost = undefined;

            if (logistics) {
                cost = logistics?.ReturnableDunnageInvestment?.Value?.Fields[0].Values.find(item => new Date(item.Date).getUTCFullYear() === year)?.Value ?? 0;
                this.ReturnableDunnageInvestmentCurrency = logistics?.ReturnableDunnageInvestment?.Value?.Fields[0]?.Tag

                let exchangeRate = exchangeRates?.find(rate => rate.year === year && rate.currency === exchangeCurrency);
                let currentCurrencyRate = exchangeRates?.find(rate => rate.year === year && rate.currency === this.ReturnableDunnageInvestmentCurrency);

                if (!(exchangeRate === undefined || cost === undefined || currentCurrencyRate === undefined)) {
                    convCost = cost * exchangeRate.value / currentCurrencyRate.value;
                    this.ReturnableDunnageInvestmentCurrencyConvCurrTotal += convCost;
                }
            }

            this.ReturnableDunnageInvestmentValues.push(cost);
            this.ReturnableDunnageInvestmentValuesConvCurr.push(convCost);
        })

        this.VolumeAllocatedTotalReturnableDunnageInvestmentConvCurr = this.ReturnableDunnageInvestmentCurrencyConvCurrTotal * this.LogisticsVolumeAllocationShare;
        let dutyPerAsm = (this.MaterialCostPerAsmConvCurr + this.FreightCostPerAsmConvCurr + this.WarehouseCostPerAsmConvCurr + this.PackagingCostPerAsmConvCurr + this.ExpendableDunnagePerAsmConvCurr) * this.DutyPercent
        this.DutyCostPerAsmConvCurr = Number.isNaN(dutyPerAsm) ? 0 : dutyPerAsm;
    }

    exchangeItem(obj, qtyPerSaleable, exchangeRates, exchangeCurrency) {
        let total = 0;
        let convTotal = 0;
        let years = [];
        let convCosts = [];
        let costs = []
        let perAsm = 0;
        let perAsmConv = 0;

        obj?.Values.forEach(item => {
            let year = new Date(item.Date).getUTCFullYear();

            years.push(year);

            let cost = item?.Value ?? undefined;
            let convCost = undefined;

            let exchangeRate = exchangeRates?.find(rate => rate.year === year && rate.currency === exchangeCurrency);
            let currentCurrencyRate = exchangeRates?.find(rate => rate.year === year && rate.currency === obj.Tag);

            if (exchangeRate === undefined || cost === undefined || currentCurrencyRate === undefined) {
                if (cost !== undefined) {
                    convCosts.push("Error");
                    costs.push("Error");
                } else {
                    convCosts.push("");
                    costs.push("");
                }
            } else {
                convCost = cost * exchangeRate.value / currentCurrencyRate.value;
                convCosts.push(convCost);
                costs.push(cost);
                convTotal += convCost;
                total += cost;
            }

        });

        perAsm = Number.isNaN(total * qtyPerSaleable) ? 0 : total * qtyPerSaleable;
        perAsmConv = Number.isNaN(convTotal * qtyPerSaleable) ? 0 : convTotal * qtyPerSaleable;

        let result = {}
        result.Total = total;
        result.ConvTotal = convTotal;
        result.Years = years;
        result.ConvCosts = convCosts;
        result.Costs = costs;
        result.PerAsm = perAsm;
        result.PerAsmConv = perAsmConv;

        return result;
    }
}